import { ChangeDetectorRef, DestroyRef, Directive, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';

/**
 * Sri: a submenu trigger's caret points where the submenu opened. Material marks the opened panel `mat-menu-before` when
 * it opens to the left (its xPosition, or flipped for room, which can land after `menuOpened`, so the panel's class is
 * watched while it is open); the trigger then gets `submenu-trigger--before`, which the tab-more-menu styles use to
 * point the chevron left, before the label (src/styles.scss).
 */
@Directive({
  selector: '[matMenuTriggerFor][appSubmenuDirection]',
  standalone: true,
  host: { '[class.submenu-trigger--before]': 'before' },
})
export class SubmenuDirectionDirective implements OnInit {
  before = false;
  private readonly trigger = inject(MatMenuTrigger, { self: true });
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private observer: MutationObserver | null = null;

  ngOnInit(): void {
    this.trigger.menuOpened.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.watch());
    this.trigger.menuClosed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.unwatch());
    this.destroyRef.onDestroy(() => this.unwatch());
  }

  private watch(): void {
    this.unwatch();
    const id = this.trigger.menu?.panelId;
    const panel = id ? document.getElementById(id) : null;
    const update = () => {
      const before = !!panel?.classList.contains('mat-menu-before');
      if (before !== this.before) {
        this.before = before;
        this.cdr.markForCheck();
      }
    };
    update();
    if (panel && typeof MutationObserver !== 'undefined') {
      this.observer = new MutationObserver(update);
      this.observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
    }
  }

  private unwatch(): void {
    this.observer?.disconnect();
    this.observer = null;
  }
}
